import type { HarmonyPackage, Iteration, TeamReferenceStory, TeamTag, Workitem, WorkitemTag } from '@rhythmic/db/types'
import type { TeamType } from '@rhythmic/types'
import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import type { Hookable } from 'hookable'
import type { Ref } from 'vue'
import type { TeamUser } from '../../../queries'

import type { TeamContextEvents } from '../useTeamContext.events.schema'

type TeamContextHookMap = {
  [K in keyof TeamContextEvents]: (event: TeamContextEvents[K]) => void | Promise<void>
}

/**
 * Handle for removing a previously registered team context hook.
 */
type TeamContextUnsubscribe = () => void

interface TeamContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface UseTeamContextOnTeamChangedParams {
  hooks: Hookable<TeamContextHookMap>
  logger: TeamContextLoggerLike
  teamQuery: Ref<UseLiveQueryReturn<TeamType> | undefined>
  iterationsQuery: Ref<UseLiveQueryReturn<Iteration> | undefined>
  harmonyPlansQuery: Ref<UseLiveQueryReturn<HarmonyPackage> | undefined>
  workitemsQuery: Ref<UseLiveQueryReturn<Workitem> | undefined>
  teamTagsQuery: Ref<UseLiveQueryReturn<TeamTag> | undefined>
  workitemTagsQuery: Ref<UseLiveQueryReturn<WorkitemTag> | undefined>
  teamMembersQuery: Ref<UseLiveQueryReturn<TeamUser> | undefined>
  teamReferenceStoryQuery: Ref<UseLiveQueryReturn<TeamReferenceStory> | undefined>
  clearTeamScopedQueries: () => void
  createIterationsQuery: (teamId: string) => UseLiveQueryReturn<Iteration> | undefined
  createHarmonyPlansQuery: (workspaceId: string, teamId: string) => UseLiveQueryReturn<HarmonyPackage> | undefined
  createWorkitemsQuery: (teamId: string) => UseLiveQueryReturn<Workitem> | undefined
  createTeamTagsQuery: (teamId: string) => UseLiveQueryReturn<TeamTag> | undefined
  createWorkitemTagsQuery: (teamId: string) => UseLiveQueryReturn<WorkitemTag> | undefined
  createTeamMembersQuery: (teamId: string, workspaceId: string) => UseLiveQueryReturn<TeamUser> | undefined
  createTeamReferenceStoriesQuery: (teamId: string) => UseLiveQueryReturn<TeamReferenceStory> | undefined
}

/**
 * Internal handler: sets up team-scoped queries once the active team is known (teamId + workspaceId).
 */
export default function useTeamContextOnTeamChanged({
  hooks,
  logger,
  teamQuery,
  iterationsQuery,
  harmonyPlansQuery,
  workitemsQuery,
  teamTagsQuery,
  workitemTagsQuery,
  teamMembersQuery,
  teamReferenceStoryQuery,
  clearTeamScopedQueries,
  createIterationsQuery,
  createHarmonyPlansQuery,
  createWorkitemsQuery,
  createTeamTagsQuery,
  createWorkitemTagsQuery,
  createTeamMembersQuery,
  createTeamReferenceStoriesQuery,
}: UseTeamContextOnTeamChangedParams) {
  let activeTeamQueryKey: string | null = null

  const unsubscribe: TeamContextUnsubscribe = hooks.hook(
    'TeamChanged',
    ({ team, teamId, teamSlug, previousTeamSlug, workspaceId }) => {
      logger.debug('Team changed, updating team context queries', {
        previousTeamSlug,
        teamSlug,
        teamId,
        workspaceId,
        teamsReady: Boolean(teamQuery.value?.isReady),
        hasTeam: Boolean(team),
      })

      if (!teamId || !workspaceId) {
        activeTeamQueryKey = null
        clearTeamScopedQueries()
        return
      }

      const nextKey = `${workspaceId}:${teamId}`
      if (activeTeamQueryKey === nextKey) return

      activeTeamQueryKey = nextKey
      iterationsQuery.value = createIterationsQuery(teamId)
      harmonyPlansQuery.value = createHarmonyPlansQuery(workspaceId, teamId)
      workitemsQuery.value = createWorkitemsQuery(teamId)
      teamTagsQuery.value = createTeamTagsQuery(teamId)
      workitemTagsQuery.value = createWorkitemTagsQuery(teamId)
      teamMembersQuery.value = createTeamMembersQuery(teamId, workspaceId)
      teamReferenceStoryQuery.value = createTeamReferenceStoriesQuery(teamId)
    },
  )

  return unsubscribe
}
